import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ChatCircleDots, Books, UsersThree, SignOut, Plus, Trash, BookmarkSimple, Sun, Moon, PaintBrush, List, X, Brain, MagicWand, DownloadSimple } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { ImportChatDialog } from "./ImportChatDialog";

export const Sidebar = ({ conversations = [], activeId, onSelect, onNew, onDelete, onToggleSave }) => {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const onChat = location.pathname === "/";

  const go = (path) => {
    setOpen(false);
    navigate(path);
  };

  const navItem = (path, icon, label, testid) => {
    const active = location.pathname === path;
    return (
      <button data-testid={testid} onClick={() => go(path)}
        className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-medium transition-colors duration-300 ${active ? "bg-[var(--hover)] text-[var(--text)]" : "text-[var(--text-2)] hover:bg-[var(--hover)] hover:text-[var(--text)]"}`}>
        {icon} {label}
      </button>
    );
  };

  return (
    <>
      <button data-testid="mobile-menu-button" onClick={() => setOpen(true)}
        className="md:hidden fixed top-3 left-3 z-40 bg-[var(--surface)] border border-[var(--border-strong)] rounded-xl p-2 text-[var(--text)] shadow-sm">
        <List size={20} />
      </button>
      {open && (
        <div data-testid="sidebar-backdrop" className="md:hidden fixed inset-0 bg-black/40 z-40" onClick={() => setOpen(false)} />
      )}
      <aside data-testid="sidebar"
        className={`fixed md:static inset-y-0 left-0 z-50 w-64 shrink-0 h-screen flex flex-col bg-[var(--surface-2)] border-r border-[var(--border)] transform transition-transform duration-300 ${open ? "translate-x-0" : "-translate-x-full"} md:translate-x-0`}>
        <div className="px-4 py-4 flex items-center gap-2.5">
          <button onClick={() => go("/")} className="flex items-center flex-1 min-w-0" data-testid="sidebar-logo" title="Exotic India">
            <img src="/exotic-india-wordmark.webp" alt="Exotic India" className="h-8 w-auto object-contain" />
          </button>
          <button data-testid="mobile-close-button" onClick={() => setOpen(false)}
            className="md:hidden ml-auto text-[var(--text-2)] hover:text-[var(--text)] p-1.5 rounded-lg">
            <X size={18} />
          </button>
        </div>

        <nav className="px-3 pb-3 space-y-0.5">
          {navItem("/", <ChatCircleDots size={17} weight={onChat ? "fill" : "regular"} />, "Chat", "nav-chat")}
          {navItem("/library", <Books size={17} weight={location.pathname === "/library" ? "fill" : "regular"} />, "Library", "nav-library")}
          {navItem("/canva", <PaintBrush size={17} weight={location.pathname === "/canva" ? "fill" : "regular"} />, "Canva", "nav-canva")}
          {navItem("/memory", <Brain size={17} weight={location.pathname === "/memory" ? "fill" : "regular"} />, "Memory", "nav-memory")}
          {navItem("/upscaler", <MagicWand size={17} weight={location.pathname === "/upscaler" ? "fill" : "regular"} />, "Upscaler", "nav-upscaler")}
          {user?.role === "admin" && navItem("/admin", <UsersThree size={17} weight={location.pathname === "/admin" ? "fill" : "regular"} />, "Team", "nav-admin")}
        </nav>

        {onChat ? (
          <>
            <div className="px-4 pt-3 pb-1.5 flex items-center justify-between border-t border-[var(--border)]">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-[var(--muted)]">Recents</span>
              <span className="flex items-center gap-0.5">
                <button data-testid="import-chat-button" onClick={() => setImportOpen(true)} title="Import from Claude / ChatGPT"
                  className="text-[var(--text-2)] hover:text-[var(--text)] hover:bg-[var(--hover)] transition-colors duration-300 p-1.5 rounded-lg">
                  <DownloadSimple size={14} weight="bold" />
                </button>
                <button data-testid="new-chat-button" onClick={() => { setOpen(false); onNew(); }} title="New chat"
                  className="text-[var(--text-2)] hover:text-[var(--text)] hover:bg-[var(--hover)] transition-colors duration-300 p-1.5 rounded-lg">
                  <Plus size={15} weight="bold" />
                </button>
              </span>
            </div>
            <div className="flex-1 overflow-y-auto px-2 pb-2 space-y-0.5">
              {conversations.length === 0 && <p className="text-xs text-[var(--muted)] px-3 py-2">No conversations yet</p>}
              {(() => {
                const pinned = conversations.filter((c) => c.saved);
                const rest = conversations.filter((c) => !c.saved);
                const groups = [];
                if (pinned.length) groups.push({ label: "Pinned", items: pinned });
                if (rest.length) groups.push({ label: pinned.length ? "All chats" : null, items: rest });
                return groups.map((g, gi) => (
                  <div key={gi}>
                    {g.label && (
                      <p className={`text-[10px] font-semibold uppercase tracking-wider text-[var(--muted)] px-3 pb-1 ${gi > 0 ? "pt-3" : "pt-1"}`}>{g.label}</p>
                    )}
                    {g.items.map((c) => (
                      <div key={c.id} data-testid={`conversation-item-${c.id}`}
                        className={`group flex items-center gap-1 px-3 py-2 rounded-xl cursor-pointer transition-colors duration-300 ${activeId === c.id ? "bg-[var(--hover)]" : "hover:bg-[var(--hover)]"}`}
                        onClick={() => { setOpen(false); onSelect(c.id); }}>
                        <span className="flex-1 min-w-0">
                          <span className={`block truncate text-[13px] transition-colors duration-300 ${activeId === c.id ? "text-[var(--text)]" : "text-[var(--text-2)] group-hover:text-[var(--text)]"}`}>{c.title}</span>
                          {c.user_id !== user?.id && (
                            <span className="block truncate text-[10px] text-[var(--muted)]">by {c.owner_name}</span>
                          )}
                        </span>
                        <button data-testid={`save-conversation-${c.id}`} title={c.saved ? "Unpin" : "Pin to top"}
                          onClick={(e) => { e.stopPropagation(); onToggleSave(c); }}
                          className={`md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-300 p-0.5 rounded ${c.saved ? "!opacity-100 text-[var(--accent)]" : "text-[var(--muted)] hover:text-[var(--text)]"}`}>
                          <BookmarkSimple size={14} weight={c.saved ? "fill" : "regular"} />
                        </button>
                        <button data-testid={`delete-conversation-${c.id}`} title="Delete"
                          onClick={(e) => { e.stopPropagation(); onDelete(c.id); }}
                          className="md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-300 text-[var(--muted)] hover:text-[var(--error)] p-0.5 rounded">
                          <Trash size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                ));
              })()}
            </div>
          </>
        ) : <div className="flex-1 border-t border-[var(--border)]" />}

        <div className="border-t border-[var(--border)] px-3 py-3 flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-[var(--surface-3)] flex items-center justify-center text-xs font-semibold text-[var(--text-2)] shrink-0">
            {user?.name?.[0]?.toUpperCase() || "?"}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-medium text-[var(--text)] truncate" data-testid="sidebar-username">{user?.name}</p>
            <p className="text-[10px] text-[var(--muted)] capitalize">{user?.role}</p>
          </div>
          <button data-testid="theme-toggle-button" onClick={toggle} title="Toggle theme"
            className="text-[var(--text-2)] hover:text-[var(--text)] hover:bg-[var(--hover)] transition-colors duration-300 p-2 rounded-lg">
            {theme === "light" ? <Moon size={16} /> : <Sun size={16} />}
          </button>
          <button data-testid="logout-button" onClick={logout} title="Logout"
            className="text-[var(--text-2)] hover:text-[var(--error)] hover:bg-[var(--hover)] transition-colors duration-300 p-2 rounded-lg">
            <SignOut size={16} />
          </button>
        </div>
      </aside>
      {importOpen && <ImportChatDialog onClose={() => setImportOpen(false)} />}
    </>
  );
};
